import React, { useEffect, useState } from "react";
import {
    Modal,
    Form,
    Input,
    Col,
    Row,
    message,
    Select,
    DatePicker,
    Button,
    InputNumber,
    Table,
} from "antd";
import { API_METHODS, BLANK_VALUE, DEFAULT_DATE_FORMAT } from "../../../constants";
import { getCreditors, getClientConfig, replaceBlankValueToUndefined, formatDateToDayJS, getDebtors, isEmpty } from "../../../generalFunctions";
import dayjs from "dayjs";
import useApi from "../../../hooks/useApi";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";

const AddCreditDebitNote = (props) => {
    const [form] = Form.useForm();
    const { visible, close, selectedRecord, isViewOnly, isDebit } = props;
    const [perticularsList, setPerticularsList] = useState([]);
    const [response, request] = useApi("/creditNote", null, API_METHODS.POST);
    const [editResponse, editRequest] = useApi(
        `/creditNote/${selectedRecord && selectedRecord.id}`,
        null,
        API_METHODS.PUT
    );
    const [singleGetResponse, singleGetRequest] = useApi(`/creditNote/`, null, API_METHODS.GET);

    const { account } = getClientConfig();
    const accounts = isDebit ? getCreditors(account) : getDebtors(account);
    const columns = [
        {
            title: 'Sr. No',
            dataIndex: 'record',
            key: 'record'
        },
        {
            title: 'Particulars',
            dataIndex: 'perticulars',
            key: 'perticulars',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
        },
        {
            title: "Action",
            key: "action",
            render: (text, record) => (
                <Button size="small" onClick={() => removePerticular(record)}>
                    Remove
                </Button>
            ),
            isViewOnly: isViewOnly
        }
    ].filter(c => !c.isViewOnly);
    const [loadingSaveButton, setLoadingSaveButton] = useState(false);

    const handleOk = () => {
        form.validateFields().then((values) => {
            if (!loadingSaveButton && perticularsList.length > 0) {
                setLoadingSaveButton(true);
                const requestBody = { ...values };
                requestBody.perticulars = JSON.stringify(perticularsList);
                requestBody.date = formatDateToDayJS(requestBody.date);
                delete requestBody.amount;
                if (selectedRecord) {
                    isDebit ? editRequest(requestBody, API_METHODS.PUT, `/debitNote/${selectedRecord && selectedRecord.id}`) : editRequest(requestBody);
                } else {
                    isDebit ? request(requestBody, API_METHODS.POST, "/debitNote") : request(requestBody);
                }
            }
            else {
                message.error("Particulars are required.");
            }
        });
    };

    const handleCancel = () => {
        close();
    };

    const addPerticulars = () => {
        const { perticulars, amount } = form.getFieldsValue();
        if (perticulars && amount) {
            const list = [...perticularsList];
            const record = { record: perticularsList.length + 1, perticulars, amount };
            list.push(record);
            setPerticularsList(list);
            form.setFieldsValue({ "perticulars": undefined, "amount": undefined });
        }
    }

    const removePerticular = (record) => {
        let list = [...perticularsList];
        const index = list.findIndex(r => r.record == record.record);
        if (index >= 0) {
            list.splice(index, 1);
            setPerticularsList(list);
        }
    }

    useEffect(() => {
        if (selectedRecord) {
            let initialValues = { ...selectedRecord };

            initialValues = replaceBlankValueToUndefined(initialValues);
            initialValues.date = formatDateToDayJS(initialValues.date);
            setPerticularsList(initialValues.perticulars);
            initialValues.perticulars = undefined;
            form.setFieldsValue(initialValues);
        }
    }, [selectedRecord]);

    useEffect(() => {
        const totalAmount = perticularsList.reduce((a, b) => +a + +b.amount, 0);
        form.setFieldValue("totalAmount", totalAmount);
    }, [perticularsList]);

    useEffect(() => {
        if (editResponse.data) {
            close(true);
            setLoadingSaveButton(false);
            message.success("Note updated successfully.");
        }
    }, [editResponse]);

    useEffect(() => {
        if (response.data) {
            close(true);
            setLoadingSaveButton(false);
            message.success("Note added successfully.");
        }
    }, [response]);

    const previousButtonClicked = () => {
        let id = form.getFieldValue("id") - 1;
        isDebit ? singleGetRequest(null, API_METHODS.GET, `/debitNote/${id}`) : singleGetRequest(null, API_METHODS.GET, `/creditNote/${id}`);

    };
    const nextButtonClicked = () => {
        let id = form.getFieldValue("id") + 1;
        isDebit ? singleGetRequest(null, API_METHODS.GET, `/debitNote/${id}`) : singleGetRequest(null, API_METHODS.GET, `/creditNote/${id}`);
    };

    useEffect(() => {
        if (singleGetResponse && !isEmpty(singleGetResponse.data) && !singleGetResponse.isLoading) {
            const { data } = singleGetResponse;
            let initialValues = data[0];
            initialValues = replaceBlankValueToUndefined(initialValues);
            initialValues.date = formatDateToDayJS(initialValues.date);
            setPerticularsList(initialValues.perticulars);
            initialValues.perticulars = undefined;
            form.setFieldsValue(initialValues);

        }
        else if (singleGetResponse && singleGetResponse.data === BLANK_VALUE && !singleGetResponse.isLoading) {
            isDebit ? message.error("Debit Note not found") : message.error("Credit Note not found");
        }
    }, [singleGetResponse]);

    return (
        <Modal
            title={isDebit ? "Debit Note" : "Credit Note"}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            width={1000}
            okText="Save"
            cancelText={isViewOnly ? "Close" : "Cancel"}
            okButtonProps={{ disabled: isViewOnly ? true : false, loading: loadingSaveButton }}
        >
            <Form
                layout="vertical"
                form={form}
                disabled={isViewOnly}
                initialValues={{
                    date: dayjs()
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="accountId"
                            label="Account"
                            rules={[{ required: true, message: "Account is required." }]}
                        >
                            <Select
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                                    0
                                }
                            >
                                {accounts.map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                        {option.accountName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="date"
                            label="Date"
                            rules={[{ required: true, message: "Date is required." }]}
                        >
                            <DatePicker format={DEFAULT_DATE_FORMAT} />
                        </Form.Item>
                    </Col>
                    {!isViewOnly && <>
                        <Col span={14}>
                            <Form.Item
                                name="perticulars"
                                label="Particulars"
                                rules={[
                                    {
                                        max: 500,
                                        message:
                                            "Particulars can be maximum of 500 characters.",
                                    },
                                ]}
                            >
                                <Input.TextArea showCount maxLength={500} />
                            </Form.Item>
                        </Col>
                        <Col span={7}>
                            <Form.Item
                                name="amount"
                                label="Amount"
                            >
                                <InputNumber />
                            </Form.Item>
                        </Col>
                        <Col span={3}>
                            <Form.Item
                                label=" "
                            >
                                <Button type="default" onClick={addPerticulars}>Add Record</Button>
                            </Form.Item>
                        </Col>
                    </>}
                </Row>
                <Row style={{ paddingTop: 20 }}>
                    <Col span={24}>
                        <Table rowKey="record" size="small" pagination={false} columns={columns} dataSource={perticularsList} />
                    </Col>
                </Row>
                <Row gutter={16} style={{ paddingTop: 20 }}>
                    <Col span={16}>
                        <Form.Item
                            name="notes"
                            label="Notes"
                            rules={[
                                {
                                    max: 500,
                                    message:
                                        "Notes can be maximum of 500 characters.",
                                },
                                { required: true, message: "Notes is required." }
                            ]}
                        >
                            <Input.TextArea showCount maxLength={500} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="totalAmount"
                            label="Total Amount"
                        >
                            <InputNumber disabled={true} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        {isViewOnly && (
            <Row justify="space-between">
                <Button type="primary" onClick={previousButtonClicked}>
                    <LeftOutlined />
                    Previous
                </Button>
                <Button type="primary" onClick={nextButtonClicked}>
                    Next
                    <RightOutlined />
                </Button>
            </Row>
            )}
        </Modal>
    );
}

export default AddCreditDebitNote;